import * as Sentry from '@sentry/browser';
import Symbol from 'es6-symbol';

import { SET_SELF_SERVICE_ERROR_EVENT_ID } from './apiError.types';
import { getSelectedClientInformation, getSelectedDivisions, jsonToQueryString } from 'utilities/common';
import Log from 'utilities/log';
import auth from 'auth/auth';
import history from 'browserHistory';

export const INTAKE_API_BASE_URL = process.env.REACT_APP_INTAKE_API_URL || 'https://testtrcinternalapi.trcgm.com/';

function callApi(token, endpoint, authenticated, method = 'GET', payload) {
  const baseUrl = INTAKE_API_BASE_URL;

  // include credentials
  // let config = { headers: {}, credentials: "include" };

  // no credentials
  let config = { headers: {} };

  if (authenticated) {
    if (token) {
      config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    } else {
      Log.trace('No token available.');
      throw new Error('No token saved!');
    }
  }
  config.method = method;
  if (typeof payload !== 'undefined') {
    //Is this form data?
    if (Object.prototype.toString.call(payload) === '[object FormData]') {
      config.body = payload;
    } else {
      config.body = JSON.stringify(payload);
      config.headers['Content-Type'] = 'application/json; charset=utf-8';
    }
  }

  // add cookie to request
  return fetch(baseUrl + endpoint, config)
    .then((response) => {
      return response.json()
        .then((json) => {
          return ({ json, response });
        })
        .catch((e) => {
          return ({
            json: [],
            response,
          });
        });
    })
    .then(({ json, response }) => {
      if (response.status === 401) {
        return Promise.reject({ authenticationRequired: true });
      }
      if (!response.ok) {
        return Promise.reject(json);
      }
      let metadata = {};
      if (response.headers.get('X-Total-Count')) {
        metadata.totalCount = Number(response.headers.get('X-Total-Count'));
      }
      return { json, metadata };
    });
}

export const CALL_INTAKE_API = Symbol('Call Intake API');

export default ({ dispatch, getState }) => (next) => (action) => {
  const callAPI = action[CALL_INTAKE_API];

  // So the middleware doesn't get applied to every single action
  if (typeof callAPI === 'undefined') {
    return next(action);
  }

  let { endpoint, types, authenticated, method, payload, options, context } = callAPI;

  const [requestType, successType, errorType] = types;

  next({
    type: requestType,
    payload,
  });

  let jwt;
  if (authenticated) {
    try {
      jwt = auth.getAccessToken();
    } catch (e) {
      Log.trace('ERROR GETTING TOKEN', e);
    }
  }

  const clientInfo = getSelectedClientInformation();
  const divisions = getSelectedDivisions().join(',');

  if (method === 'GET') {
    const queryParams = {};
    if (options) {
      // client specific
      if (options.isClientSpecific) {
        if (clientInfo && clientInfo.id) {
          queryParams.clientId = clientInfo.id;
        }
      }
      // division specific
      if (options.isDivisionSpecific) {
        if (divisions) {
          queryParams.divisionList = divisions;
        }
      }
      endpoint += jsonToQueryString(queryParams);
    }
  }

  if (method === 'POST') {
    if (options) {
      // client specific
      if (options.isClientSpecific) {
        if (clientInfo && clientInfo.id) {
          payload = { 'ClientID': clientInfo.id, ...payload };
        }
      }
      // division specific
      if (options.isDivisionSpecific) {
        if (divisions) {
          payload = { 'DivisionList': divisions, ...payload };
        }
      }
    }
  }

  // Passing the authenticated boolean back in our data will let us distinguish
  // between normal and secret quotes
  return callApi(jwt, endpoint, authenticated, method, payload).then(
    (response) => {
      return next({
        response: response.json,
        metadata: response.metadata,
        authenticated,
        payload,
        type: successType,
        options,
        context,
      });
    },
    async (error) => {
      const resp = {
        type: errorType,
        context,
      };
      if (error.messages) {
        resp.messages = error.messages;
      } else if (error.message) {
        resp.messages = [error.message];
      } else {
        resp.messages = ['There was an error.'];
      }

      Sentry.captureException(new Error(`API Error: ${JSON.stringify(resp)}`));
      dispatch({ type: SET_SELF_SERVICE_ERROR_EVENT_ID, payload: error.eventId });

      const nextRes = next(resp);
      if (error.authenticationRequired) {
        const wasRenewed = await auth.renewSession();
        if (!wasRenewed) {
          history.replace('/login', {
            from: { pathname: history.location.pathname },
          });
        }
      }
      return nextRes;
    },
  );
};
